import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, MailCheck } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Step 1 of password reset — collect email and ask the backend to send a
 * reset email containing both a one-click link AND a 6-digit code.
 *
 * The endpoint always returns 200 (to prevent email enumeration), so we
 * always show the same "check your inbox" success state regardless of
 * whether the email is registered. Users can still navigate to the
 * code-entry screen from here if they prefer that path.
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setSent(true);
    } catch (error) {
      // The endpoint shouldn't 4xx unless email is malformed.
      toast.error(error.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background luxury-mesh-bg flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex flex-col items-center gap-3">
            <img src="/wemillionaires-logo.png" alt="We Masterminds" className="h-16 w-auto" />
            <h1 className="text-3xl font-playfair font-bold text-foreground tracking-wide">WE MASTERMINDS</h1>
          </Link>
          <p className="text-muted-foreground mt-2">Reset your password</p>
        </div>

        <div className="wm-card p-8" data-testid="forgot-password-card">
          {sent ? (
            <div className="text-center space-y-4">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-500/10 text-emerald-400 mx-auto">
                <MailCheck className="w-7 h-7" />
              </div>
              <h2 className="text-xl font-semibold text-foreground">Check your inbox</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                If an account exists for <span className="text-foreground font-medium">{email}</span>,
                we just sent you an email with a one-click reset link and a 6-digit code.
                The link and code expire in 15 minutes.
              </p>
              <div className="pt-2 flex flex-col gap-2">
                <Link
                  to={`/reset-password?email=${encodeURIComponent(email)}`}
                  className="text-gold hover:text-gold/80 transition-colors text-sm font-medium"
                  data-testid="enter-code-link"
                >
                  Enter your 6-digit code instead →
                </Link>
                <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Back to sign in
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="forgot-email" className="wm-label">Email</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="you@example.com"
                  className="mt-2 bg-[#0a0a12] border-white/10 text-foreground rounded-md h-12 focus:border-gold focus:ring-1 focus:ring-gold/30"
                  data-testid="forgot-email-input"
                />
                <p className="mt-2 text-xs text-muted-foreground">
                  We'll send you a one-click reset link and a 6-digit code. Both work — use whichever is faster.
                </p>
              </div>

              <Button
                type="submit"
                disabled={loading || !email}
                className="w-full h-12 bg-gold-gradient text-black hover:shadow-gold-glow font-semibold uppercase tracking-widest rounded-md"
                data-testid="forgot-submit-btn"
              >
                {loading ? 'Sending…' : 'Send reset email'}
              </Button>
            </form>
          )}

          {!sent && (
            <div className="mt-6 text-center">
              <Link
                to="/login"
                className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
                data-testid="back-to-login-link"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
